/* Build-Your-Hamper picker (multi-instance safe) + Magic Cart aggregator.
   - Any element with [data-hamper-root] becomes a self-contained picker.
   - Each picker emits "hamper:update" and "hamper:added" events on itself.
   - A single [data-magic-cart] on the page aggregates all active pickers'
     progress and surfaces a sticky summary with scroll/Add actions. */
(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Utilities
     ------------------------------------------------------------------ */
  var ESCAPE_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  var ESCAPE_RE = /[&<>"']/g;
  function escapeHtml(s) {
    if (s == null) return '';
    return String(s).replace(ESCAPE_RE, function (c) { return ESCAPE_MAP[c]; });
  }

  function thumbUrl(src, width) {
    if (!src) return '';
    var sep = src.indexOf('?') > -1 ? '&' : '?';
    return src + sep + 'width=' + width;
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var self = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, ms);
    };
  }

  /* ------------------------------------------------------------------
     Collection fetch — parallel pages, cached per handle
     ------------------------------------------------------------------ */
  var PAGE_SIZE = 250;
  var fetchCache = {};

  function fetchCollectionPage(handle, page) {
    var url = '/collections/' + encodeURIComponent(handle) +
              '/products.json?limit=' + PAGE_SIZE + '&page=' + page;
    return fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    }).then(function (data) { return (data && data.products) || []; });
  }

  function fetchAllProducts(handle, total) {
    if (fetchCache[handle]) return fetchCache[handle];
    var pageCount = Math.max(1, Math.ceil((total || PAGE_SIZE) / PAGE_SIZE));
    var requests = [];
    for (var p = 1; p <= pageCount; p++) requests.push(fetchCollectionPage(handle, p));
    fetchCache[handle] = Promise.all(requests).then(function (pages) {
      return pages.reduce(function (acc, items) { return acc.concat(items); }, []);
    });
    return fetchCache[handle];
  }

  /* ------------------------------------------------------------------
     Render — fragment-based, chunked beyond the first viewport
     ------------------------------------------------------------------ */
  var CHECK_SVG = '<svg viewBox="0 0 20 20" width="12" height="12"><path d="M4 10l4 4 8-8" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var EYE_SVG = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>';
  var FIRST_CHUNK = 60;

  function pickButtonHtml(p) {
    var variant = (p.variants && p.variants[0]) || null;
    if (!variant) return '';
    var available = variant.available !== false;
    var img = (p.images && p.images[0]) || null;
    var titleEsc = escapeHtml(p.title);
    var priceHtml = available
      ? '<span class="hamper-pick__price">' + escapeHtml(formatMoney(variant.price)) + '</span>'
      : '';
    var media = img && img.src
      ? '<img src="' + thumbUrl(img.src, 240) + '"' +
        ' srcset="' + thumbUrl(img.src, 180) + ' 180w, ' + thumbUrl(img.src, 320) + ' 320w"' +
        ' sizes="120px" alt="" loading="lazy" decoding="async">'
      : '<span class="placeholder-svg hamper-pick__placeholder"></span>';
    return (
      '<button type="button" class="hamper-pick" role="listitem" data-hamper-item' +
      ' data-variant-id="' + variant.id + '"' +
      ' data-product-title="' + titleEsc + '"' +
      ' data-search="' + titleEsc.toLowerCase() + '"' +
      ' aria-pressed="false"' +
      (available ? '' : ' disabled aria-disabled="true"') +
      '><span class="hamper-pick__media">' + media +
        '<span class="hamper-pick__check" aria-hidden="true">' + CHECK_SVG + '</span>' +
        '<span class="hamper-pick__view" data-hamper-view role="button" tabindex="0" aria-label="Quick view ' + titleEsc + '">' + EYE_SVG + '</span>' +
      '</span><span class="hamper-pick__title">' + titleEsc + '</span>' + priceHtml + '</button>'
    );
  }

  function appendProducts(picker, products) {
    if (!products.length) return;
    var frag = document.createRange().createContextualFragment(
      products.map(pickButtonHtml).join('')
    );
    picker.appendChild(frag);
  }

  function renderProducts(picker, products) {
    if (!products.length) {
      picker.innerHTML = '<p class="hamper__picker-loading">No products available in this collection.</p>';
      return;
    }
    // Clear loader and render first chunk synchronously, remainder on idle.
    picker.textContent = '';
    appendProducts(picker, products.slice(0, FIRST_CHUNK));
    var remaining = products.slice(FIRST_CHUNK);
    if (!remaining.length) return;
    var render = function () { appendProducts(picker, remaining); };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(render, { timeout: 500 });
    } else {
      setTimeout(render, 0);
    }
  }

  function renderError(picker, root) {
    picker.innerHTML =
      '<div class="hamper__picker-error-state">' +
        '<p>Couldn\'t load products.</p>' +
        '<button type="button" class="btn btn--outline" data-hamper-retry>Try again</button>' +
      '</div>';
    var retry = picker.querySelector('[data-hamper-retry]');
    if (retry) retry.addEventListener('click', function () {
      delete fetchCache[root.getAttribute('data-pick-collection')];
      loadPicker(root);
    });
  }

  /* ------------------------------------------------------------------
     Search filter — hides non-matching items via class, no re-render
     ------------------------------------------------------------------ */
  function wireSearch(root, picker) {
    var input = root.querySelector('[data-hamper-search]');
    var countEl = root.querySelector('[data-hamper-visible-count]');
    if (!input) return;

    function apply() {
      var q = input.value.trim().toLowerCase();
      var items = picker.querySelectorAll('[data-hamper-item]');
      var shown = 0;
      items.forEach(function (btn) {
        var match = !q || btn.getAttribute('data-search').indexOf(q) > -1;
        btn.classList.toggle('is-filtered-out', !match);
        if (match) shown++;
      });
      if (countEl) countEl.textContent = String(shown);
    }
    input.addEventListener('input', debounce(apply, 120));
  }

  /* ------------------------------------------------------------------
     Quick-view modal (single shared instance across pickers)
     ------------------------------------------------------------------ */
  var qvState = { modal: null, pickBtn: null, root: null, lastFocus: null };

  function stripHtml(html) {
    if (!html) return '';
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
  }

  function formatMoney(amount) {
    // /collections/:handle/products.json returns variant.price in major
    // currency units (for example "299.00"), not paise.
    var value = Number(amount);
    if (!Number.isFinite(value)) return '';
    return '₹' + value.toLocaleString('en-IN', {
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2
    });
  }

  function openQuickView(root, product, pickBtn) {
    var modal = document.querySelector('[data-hamper-qv]');
    if (!modal) return;
    qvState.modal = modal;
    qvState.root = root;
    qvState.pickBtn = pickBtn;
    qvState.lastFocus = document.activeElement;

    var titleEl = modal.querySelector('[data-hamper-qv-title]');
    var priceEl = modal.querySelector('[data-hamper-qv-price]');
    var descEl = modal.querySelector('[data-hamper-qv-desc]');
    var imgEl = modal.querySelector('[data-hamper-qv-image]');
    var pdpLink = modal.querySelector('[data-hamper-qv-pdp]');
    var addBtn = modal.querySelector('[data-hamper-qv-add]');

    if (titleEl) titleEl.textContent = product.title || '';
    var variant = (product.variants && product.variants[0]) || null;
    if (priceEl && variant) priceEl.textContent = formatMoney(variant.price);
    if (descEl) {
      var short = stripHtml(product.body_html || '').slice(0, 320);
      descEl.textContent = short + (short.length === 320 ? '…' : '');
    }
    var img = (product.images && product.images[0]) || null;
    if (imgEl) {
      if (img && img.src) {
        imgEl.src = thumbUrl(img.src, 900);
        imgEl.srcset = thumbUrl(img.src, 600) + ' 600w, ' + thumbUrl(img.src, 900) + ' 900w, ' + thumbUrl(img.src, 1200) + ' 1200w';
        imgEl.alt = product.title || '';
      } else {
        imgEl.removeAttribute('src');
        imgEl.alt = '';
      }
    }
    if (pdpLink && product.handle) pdpLink.href = '/products/' + product.handle;

    // Reflect current selection state on the Add button
    if (addBtn) {
      var isSelected = pickBtn.classList.contains('is-selected');
      addBtn.textContent = isSelected ? 'Remove from hamper' : 'Add to hamper';
      addBtn.classList.toggle('is-remove', isSelected);
      if (variant && variant.available === false) {
        addBtn.disabled = true;
        addBtn.textContent = 'Sold out';
      } else {
        addBtn.disabled = false;
      }
    }

    modal.hidden = false;
    requestAnimationFrame(function () { modal.classList.add('is-open'); });
    document.body.classList.add('is-hamper-qv-open');
    var closeBtn = modal.querySelector('[data-hamper-qv-close]');
    if (closeBtn) closeBtn.focus();
    document.addEventListener('keydown', onQvKey);
  }

  function closeQuickView() {
    var modal = qvState.modal;
    if (!modal) return;
    modal.classList.remove('is-open');
    setTimeout(function () { modal.hidden = true; }, 180);
    document.body.classList.remove('is-hamper-qv-open');
    document.removeEventListener('keydown', onQvKey);
    if (qvState.lastFocus && qvState.lastFocus.focus) qvState.lastFocus.focus();
    qvState = { modal: null, pickBtn: null, root: null, lastFocus: null };
  }

  function onQvKey(e) {
    if (e.key === 'Escape') { e.preventDefault(); closeQuickView(); }
  }

  function initQuickViewModal() {
    var modal = document.querySelector('[data-hamper-qv]');
    if (!modal) return;
    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-hamper-qv-close]')) { e.preventDefault(); closeQuickView(); return; }
      var addBtn = e.target.closest('[data-hamper-qv-add]');
      if (addBtn && qvState.pickBtn) {
        e.preventDefault();
        qvState.pickBtn.click();  // triggers toggleItem via picker's existing listener
        closeQuickView();
      }
    });
  }

  /* ------------------------------------------------------------------
     Picker lifecycle
     ------------------------------------------------------------------ */
  function loadPicker(root) {
    var picker = root.querySelector('[data-hamper-picker]');
    var handle = root.getAttribute('data-pick-collection');
    var total = parseInt(root.getAttribute('data-pick-total'), 10) || 0;
    if (!picker || !handle) return;
    picker.innerHTML = '<div class="hamper__picker-loading" data-hamper-loading>Loading ' + total + ' products…</div>';
    fetchAllProducts(handle, total)
      .then(function (products) {
        // Store a variant_id -> product lookup for quick-view
        var map = {};
        products.forEach(function (p) {
          var v = (p.variants && p.variants[0]) || null;
          if (v) map[String(v.id)] = p;
        });
        root.__productsByVariant = map;

        renderProducts(picker, products);
        wireSearch(root, picker);
        var countEl = root.querySelector('[data-hamper-visible-count]');
        if (countEl) countEl.textContent = String(products.length);
        var totalEl = root.querySelector('[data-hamper-total-count]');
        if (totalEl) totalEl.textContent = String(products.length);
      })
      .catch(function () { renderError(picker, root); });
  }

  function initRoot(root) {
    if (root.__hamperInit) return;
    root.__hamperInit = true;

    var required = parseInt(root.getAttribute('data-required'), 10) || 1;
    var bundleLabel = root.getAttribute('data-bundle-label') || 'Hamper';
    var hamperId = root.getAttribute('data-hamper-id') || ('h' + Math.random().toString(36).slice(2, 8));
    var pickCollection = root.getAttribute('data-pick-collection');
    var picker = root.querySelector('[data-hamper-picker]');
    var counterEl = root.querySelector('[data-hamper-selected]');
    var barEl = root.querySelector('[data-hamper-bar]');
    var addBtn = root.querySelector('[data-hamper-add]');
    var hintEl = root.querySelector('[data-hamper-hint]');

    if (!picker || !addBtn) return;

    var selected = [];

    if (pickCollection && picker.querySelector('[data-hamper-loading]')) {
      loadPicker(root);
    } else {
      wireSearch(root, picker);
    }

    function emit(name, extra) {
      var detail = Object.assign({
        id: hamperId,
        label: bundleLabel,
        count: selected.length,
        required: required,
        ready: selected.length === required,
        root: root
      }, extra || {});
      root.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
    }

    function updateUI() {
      var count = selected.length;
      if (counterEl) counterEl.textContent = String(count);
      var pct = Math.min(100, Math.round((count / required) * 100));
      if (barEl) barEl.style.width = pct + '%';
      var ready = count === required;
      addBtn.disabled = !ready;
      addBtn.setAttribute('aria-disabled', ready ? 'false' : 'true');
      if (hintEl) {
        if (ready) {
          hintEl.textContent = 'Ready to add ' + count + ' items.';
          hintEl.classList.add('is-ready');
        } else {
          hintEl.textContent = 'Select ' + (required - count) + ' more to continue.';
          hintEl.classList.remove('is-ready');
        }
      }
      emit('hamper:update');
    }

    function toggleItem(btn) {
      if (btn.disabled) return;
      var id = btn.getAttribute('data-variant-id');
      if (!id) return;
      var idx = selected.indexOf(id);
      if (idx > -1) {
        selected.splice(idx, 1);
        btn.setAttribute('aria-pressed', 'false');
        btn.classList.remove('is-selected');
      } else {
        if (selected.length >= required) {
          btn.classList.add('is-shake');
          setTimeout(function () { btn.classList.remove('is-shake'); }, 420);
          if (hintEl) hintEl.textContent = 'Hamper is full. Remove an item to swap.';
          return;
        }
        selected.push(id);
        btn.setAttribute('aria-pressed', 'true');
        btn.classList.add('is-selected');
      }
      updateUI();
    }

    picker.addEventListener('click', function (e) {
      // Eye-icon → open quick-view (don't toggle selection)
      var viewTrigger = e.target.closest('[data-hamper-view]');
      if (viewTrigger) {
        e.preventDefault();
        e.stopPropagation();
        var pickBtn = viewTrigger.closest('[data-hamper-item]');
        if (!pickBtn) return;
        var variantId = pickBtn.getAttribute('data-variant-id');
        var product = root.__productsByVariant && root.__productsByVariant[variantId];
        if (product) openQuickView(root, product, pickBtn);
        return;
      }
      var btn = e.target.closest('[data-hamper-item]');
      if (btn && picker.contains(btn)) toggleItem(btn);
    });
    // Keyboard: Enter/Space on eye icon opens quick-view
    picker.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var viewTrigger = e.target.closest('[data-hamper-view]');
      if (!viewTrigger) return;
      e.preventDefault();
      e.stopPropagation();
      var pickBtn = viewTrigger.closest('[data-hamper-item]');
      if (!pickBtn) return;
      var variantId = pickBtn.getAttribute('data-variant-id');
      var product = root.__productsByVariant && root.__productsByVariant[variantId];
      if (product) openQuickView(root, product, pickBtn);
    });

    function showError(msg) {
      var tmpl = root.querySelector('[data-hamper-error-tmpl]');
      var existing = root.querySelector('.hamper-pdp__error, .hamper__picker-error');
      if (existing) existing.remove();
      if (!tmpl || !tmpl.content.firstElementChild) return;
      var node = tmpl.content.firstElementChild.cloneNode(true);
      node.textContent = msg;
      addBtn.parentNode.insertBefore(node, addBtn.nextSibling);
    }

    function refreshCartDrawer() {
      return fetch('/?section_id=cart-drawer', {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          if (!html) return;
          var host = document.getElementById('cart');
          if (!host) return;
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var next = parsed.querySelector('#cart');
          if (next) host.innerHTML = next.innerHTML;
        })
        .catch(function () { /* non-fatal */ });
    }

    function openCartDrawer() {
      var trigger = document.querySelector('[data-open="cart"]');
      if (trigger) trigger.click();
    }

    function clearSelections() {
      selected = [];
      picker.querySelectorAll('[data-hamper-item].is-selected').forEach(function (b) {
        b.setAttribute('aria-pressed', 'false');
        b.classList.remove('is-selected');
      });
      updateUI();
    }

    function submitHamper() {
      if (selected.length !== required || addBtn.disabled) return;
      addBtn.disabled = true;
      addBtn.setAttribute('aria-busy', 'true');
      addBtn.classList.add('is-loading');

      // Prefer parent-product mode: single line item + picks as properties.
      // Falls back to individual line items if no parent product is linked.
      var parentVariantId = root.getAttribute('data-parent-variant-id');
      var items;
      if (parentVariantId) {
        var properties = { _bundle: bundleLabel };
        selected.forEach(function (variantId, idx) {
          var product = root.__productsByVariant && root.__productsByVariant[variantId];
          var title = product ? product.title : ('Item ' + (idx + 1));
          properties['Item ' + (idx + 1)] = title;
          properties['_item_' + (idx + 1) + '_variant_id'] = String(variantId);
        });
        items = [{ id: parseInt(parentVariantId, 10), quantity: 1, properties: properties }];
      } else {
        items = selected.map(function (id) {
          return { id: parseInt(id, 10), quantity: 1, properties: { _bundle: bundleLabel } };
        });
      }

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (resp) {
          if (!resp.ok) {
            var msg = (resp.data && (resp.data.description || resp.data.message)) || 'Could not add hamper to bag.';
            showError(msg);
            return;
          }
          emit('hamper:added');
          clearSelections();
          return fetch('/cart.js', { credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              var bag = document.querySelector('[data-bag-count]');
              if (bag) bag.textContent = '(' + cart.item_count + ')';
            })
            .then(refreshCartDrawer)
            .then(openCartDrawer);
        })
        .catch(function () { showError('Network error. Please try again.'); })
        .finally(function () {
          addBtn.disabled = false;
          addBtn.removeAttribute('aria-busy');
          addBtn.classList.remove('is-loading');
        });
    }

    addBtn.addEventListener('click', submitHamper);
    updateUI();
    root.__hamperSubmit = submitHamper;
  }

  /* ------------------------------------------------------------------
     Magic Cart aggregator
     ------------------------------------------------------------------ */
  function initMagicCart() {
    var cart = document.querySelector('[data-magic-cart]');
    if (!cart) return;
    var list = cart.querySelector('[data-magic-list]');
    var tmpl = cart.querySelector('[data-magic-row-tmpl]');
    if (!list || !tmpl || !tmpl.content.firstElementChild) return;

    function rowFor(id) {
      return list.querySelector('[data-magic-row][data-id="' + CSS.escape(id) + '"]');
    }
    function syncVisibility() { cart.hidden = list.children.length === 0; }

    function upsertRow(detail) {
      var row = rowFor(detail.id);
      if (!row) {
        row = tmpl.content.firstElementChild.cloneNode(true);
        row.setAttribute('data-id', detail.id);
        list.appendChild(row);
      }
      row.querySelector('[data-magic-name]').textContent = detail.label;
      var pct = Math.min(100, Math.round((detail.count / detail.required) * 100));
      row.querySelector('[data-magic-bar]').style.width = pct + '%';
      row.querySelector('[data-magic-count]').textContent = detail.count + ' / ' + detail.required;
      var cta = row.querySelector('[data-magic-cta]');
      if (detail.ready) {
        cta.textContent = 'Add to Cart';
        cta.setAttribute('data-action', 'add');
        row.classList.add('is-ready');
      } else {
        cta.textContent = 'Pick ' + (detail.required - detail.count) + ' more';
        cta.setAttribute('data-action', 'scroll');
        row.classList.remove('is-ready');
      }
      syncVisibility();
    }

    function removeRow(id) {
      var row = rowFor(id);
      if (row) row.remove();
      syncVisibility();
    }

    document.addEventListener('hamper:update', function (e) {
      if (!e.detail) return;
      if (e.detail.count === 0) removeRow(e.detail.id); else upsertRow(e.detail);
    });
    document.addEventListener('hamper:added', function (e) {
      if (e.detail && e.detail.id) removeRow(e.detail.id);
    });
    list.addEventListener('click', function (e) {
      var cta = e.target.closest('[data-magic-cta]');
      if (!cta) return;
      var row = cta.closest('[data-magic-row]');
      if (!row) return;
      var id = row.getAttribute('data-id');
      var target = document.querySelector('[data-hamper-id="' + CSS.escape(id) + '"]');
      if (!target) return;
      var action = cta.getAttribute('data-action');
      if (action === 'add' && typeof target.__hamperSubmit === 'function') {
        target.__hamperSubmit();
      } else {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.classList.add('is-magic-flash');
        setTimeout(function () { target.classList.remove('is-magic-flash'); }, 1200);
      }
    });
    syncVisibility();
  }

  function initAll() {
    document.querySelectorAll('[data-hamper-root]').forEach(initRoot);
    initMagicCart();
    initQuickViewModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
